import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from '@/shared/utils/debounce';
import type {
  UseDebouncedSearchOptions,
  UseDebouncedSearchReturn,
} from './useDebouncedSearch.types';

export function useDebouncedSearch({
  initialValue = '',
  minLength = 3,
  delay = 500,
  onSearchApplied,
}: UseDebouncedSearchOptions = {}): UseDebouncedSearchReturn {
  const [searchValue, setSearchValue] = useState(initialValue);
  const [searchQuery, setSearchQuery] = useState(initialValue);

  const debouncedSearchUpdate = useMemo(
    () =>
      debounce((value: string, onApplied?: () => void) => {
        if (value === '' || value.length >= minLength) {
          setSearchQuery(value);
          onApplied?.();
        }
      }, delay),
    [delay, minLength]
  );

  useEffect(() => {
    return () => debouncedSearchUpdate.cancel();
  }, [debouncedSearchUpdate]);

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      debouncedSearchUpdate(value, onSearchApplied);
    },
    [debouncedSearchUpdate, onSearchApplied]
  );

  return {
    searchValue,
    searchQuery,
    onSearchChange,
  };
}
